import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Mail, AlertCircle, CheckCircle, RefreshCw, MessageSquare, Send, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import ThemeToggle from '../components/ThemeToggle';
import axios from 'axios';
import BrandLogo from '../components/BrandLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const CorrectionsPage = () => {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [submitMessage, setSubmitMessage] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    complaint_type: 'correction',
    output_reference: '',
    description: '',
    evidence: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      // Build payload, excluding empty optional fields
      const payload = {
        complaint_type: formData.complaint_type,
        description: formData.description,
      };
      
      // Only include optional fields if they have values
      if (formData.name && formData.name.trim()) {
        payload.name = formData.name.trim();
      }
      if (formData.email && formData.email.trim()) {
        payload.email = formData.email.trim();
      }
      if (formData.output_reference && formData.output_reference.trim()) {
        payload.output_reference = formData.output_reference.trim();
      }
      if (formData.evidence && formData.evidence.trim()) {
        payload.evidence = formData.evidence.trim();
      }
      
      const response = await axios.post(`${BACKEND_URL}/api/complaints/submit`, payload);
      setSubmitStatus('success');
      setSubmitMessage(`Thank you for your submission. Reference ID: ${response.data.complaint_id}`);
      setFormData({
        name: '',
        email: '',
        complaint_type: 'correction',
        output_reference: '',
        description: '',
        evidence: ''
      });
    } catch (err) {
      console.error('Complaint submission error:', err);
      setSubmitStatus('error');
      setSubmitMessage(err.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md sticky top-0 z-40">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <BrandLogo size="responsive" data-testid="brand-logo-header" />
            <span className="text-lg sm:text-xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 dark:from-blue-400 dark:to-blue-600 bg-clip-text text-transparent">
              KaiCheckIt
            </span>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <ThemeToggle />
            <Button variant="outline" size="sm" onClick={() => navigate('/transparency')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-3xl mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 rounded-lg bg-blue-100 dark:bg-blue-900/50">
                <FileText className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300">
                Policy Document
              </Badge>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-800 dark:text-slate-100 mb-4">
              Corrections & Complaints Policy
            </h1>
            <p className="text-slate-600 dark:text-slate-400">
              Last updated: February 2026
            </p>
          </div>

          {/* Submit Button - Prominent */}
          <Card className="mb-8 bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 border-blue-200 dark:border-blue-700">
            <CardContent className="pt-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div>
                  <h3 className="font-semibold text-blue-800 dark:text-blue-300">Have a correction or complaint?</h3>
                  <p className="text-blue-700 dark:text-blue-400 text-sm">All submissions are reviewed by our team.</p>
                </div>
                <Button 
                  onClick={() => setShowForm(!showForm)}
                  className="bg-blue-600 hover:bg-blue-700 whitespace-nowrap"
                  data-testid="toggle-complaint-form-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {showForm ? 'Hide Form' : 'Submit Now'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Submission Form */}
          {showForm && (
            <Card className="mb-8 dark:bg-slate-800 dark:border-slate-700">
              <CardHeader>
                <CardTitle className="dark:text-slate-100">Submit a Correction or Complaint</CardTitle>
                <CardDescription className="dark:text-slate-400">
                  Your submission will be reviewed by our team. Anonymous submissions are accepted.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {submitStatus === 'success' && (
                  <Alert className="mb-6 bg-green-50 border-green-200 dark:bg-green-900/30 dark:border-green-800" data-testid="complaint-success-alert">
                    <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                    <AlertDescription className="text-green-800 dark:text-green-300">
                      {submitMessage}
                    </AlertDescription>
                  </Alert>
                )}
                
                {submitStatus === 'error' && (
                  <Alert className="mb-6 bg-red-50 border-red-200 dark:bg-red-900/30 dark:border-red-800" data-testid="complaint-error-alert">
                    <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
                    <AlertDescription className="text-red-800 dark:text-red-300">
                      {submitMessage}
                    </AlertDescription>
                  </Alert>
                )}

                <form onSubmit={handleSubmit} className="space-y-4" data-testid="complaint-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name" className="dark:text-slate-200">Name (optional)</Label>
                      <Input
                        id="name"
                        data-testid="complaint-name-input"
                        placeholder="Your name or 'Anonymous'"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="dark:text-slate-200">Email (optional)</Label>
                      <Input
                        id="email"
                        type="email"
                        data-testid="complaint-email-input"
                        placeholder="For follow-up responses"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="type" className="dark:text-slate-200">Submission Type *</Label>
                    <Select
                      value={formData.complaint_type}
                      onValueChange={(value) => setFormData({...formData, complaint_type: value})}
                    >
                      <SelectTrigger className="mt-1" data-testid="complaint-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="correction" data-testid="type-correction">Correction Request</SelectItem>
                        <SelectItem value="complaint" data-testid="type-complaint">Complaint</SelectItem>
                        <SelectItem value="appeal" data-testid="type-appeal">Appeal (Secondary Review)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="reference" className="dark:text-slate-200">Output Reference (optional)</Label>
                    <Input
                      id="reference"
                      data-testid="complaint-reference-input"
                      placeholder="Verification ID, URL, or description of the output"
                      value={formData.output_reference}
                      onChange={(e) => setFormData({...formData, output_reference: e.target.value})}
                      className="mt-1"
                    />
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      Help us identify the specific output you're referencing
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="description" className="dark:text-slate-200">Description *</Label>
                    <Textarea
                      id="description"
                      data-testid="complaint-description-input"
                      placeholder="Please describe the issue in detail..."
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      className="mt-1 min-h-[120px]"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="evidence" className="dark:text-slate-200">Supporting Evidence (optional)</Label>
                    <Textarea
                      id="evidence"
                      data-testid="complaint-evidence-input"
                      placeholder="Links, sources, or other evidence supporting your submission..."
                      value={formData.evidence}
                      onChange={(e) => setFormData({...formData, evidence: e.target.value})}
                      className="mt-1 min-h-[80px]"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    data-testid="complaint-submit-btn"
                    disabled={isSubmitting || !formData.description}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Submit
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Content */}
          <div className="space-y-8">
            {/* Purpose */}
            <section>
              <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                Purpose
              </h2>
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardContent className="pt-6">
                  <p className="text-slate-700 dark:text-slate-300">
                    KaiCheckIt is committed to accuracy, transparency, and accountability. This policy outlines how errors, complaints, and disputes relating to KaiCheckIt outputs are handled.
                  </p>
                </CardContent>
              </Card>
            </section>

            {/* Scope */}
            <section>
              <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                Scope
              </h2>
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardContent className="pt-6 space-y-4">
                  <div>
                    <h3 className="font-medium text-slate-800 dark:text-slate-200 mb-2">This policy applies to:</h3>
                    <ul className="list-disc list-inside text-slate-700 dark:text-slate-300 space-y-1">
                      <li>Verification summaries and classifications</li>
                      <li>Source references used in outputs</li>
                      <li>Explanatory or contextual language generated by the platform</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-medium text-slate-800 dark:text-slate-200 mb-2">It does not apply to:</h3>
                    <ul className="list-disc list-inside text-slate-700 dark:text-slate-300 space-y-1">
                      <li>Third-party content beyond KaiCheckIt's control</li>
                      <li>Clearly labeled opinion or interpretation</li>
                      <li>User prompts that do not affect output integrity</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* How to Submit */}
            <section>
              <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                How to Submit a Complaint
              </h2>
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardContent className="pt-6 space-y-4">
                  <p className="text-slate-700 dark:text-slate-300">
                    Any individual or organization may submit a complaint or request a correction using the form above.
                  </p>
                  <div>
                    <h3 className="font-medium text-slate-800 dark:text-slate-200 mb-2">Submissions should include:</h3>
                    <ul className="list-disc list-inside text-slate-700 dark:text-slate-300 space-y-1">
                      <li>The specific output or claim in question</li>
                      <li>A description of the perceived issue</li>
                      <li>Supporting evidence or sources, where available</li>
                    </ul>
                  </div>
                  <p className="text-slate-700 dark:text-slate-300">
                    Anonymous complaints are accepted, though follow-up may be limited.
                  </p>
                </CardContent>
              </Card>
            </section>

            {/* Review Process */}
            <section>
              <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2">
                <RefreshCw className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                Review Process
              </h2>
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardContent className="pt-6">
                  <ol className="space-y-4">
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center text-sm font-medium">1</span>
                      <div>
                        <strong className="text-slate-800 dark:text-slate-200">Acknowledgment</strong>
                        <span className="text-slate-600 dark:text-slate-400"> of receipt</span>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center text-sm font-medium">2</span>
                      <div>
                        <strong className="text-slate-800 dark:text-slate-200">Assessment</strong>
                        <span className="text-slate-600 dark:text-slate-400"> of sources, classification logic, and explanation</span>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center text-sm font-medium">3</span>
                      <div>
                        <strong className="text-slate-800 dark:text-slate-200">Determination</strong>
                        <span className="text-slate-600 dark:text-slate-400">, resulting in: No action, Clarification, Correction, or Reclassification/withdrawal</span>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center text-sm font-medium">4</span>
                      <div>
                        <strong className="text-slate-800 dark:text-slate-200">Documentation</strong>
                        <span className="text-slate-600 dark:text-slate-400"> for audit and quality improvement</span>
                      </div>
                    </li>
                  </ol>
                </CardContent>
              </Card>
            </section>

            {/* Corrections */}
            <section>
              <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                Corrections
              </h2>
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardContent className="pt-6 space-y-4">
                  <p className="text-slate-700 dark:text-slate-300">When a correction is required:</p>
                  <ul className="list-disc list-inside text-slate-700 dark:text-slate-300 space-y-1">
                    <li>It is made promptly</li>
                    <li>The correction is clearly indicated</li>
                    <li>An explanation is provided where appropriate</li>
                  </ul>
                  <p className="text-slate-700 dark:text-slate-300 font-medium border-l-4 border-blue-500 pl-4 bg-blue-50 dark:bg-blue-900/20 py-2">
                    KaiCheckIt does not alter outputs due to political, reputational, or commercial pressure.
                  </p>
                </CardContent>
              </Card>
            </section>

            {/* Appeals */}
            <section>
              <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                Appeals
              </h2>
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardContent className="pt-6">
                  <p className="text-slate-700 dark:text-slate-300">
                    Complainants may request a secondary review if new evidence or reasoning is provided. Appeals are assessed independently where feasible.
                  </p>
                </CardContent>
              </Card>
            </section>
          </div>

          {/* Back Link */}
          <div className="mt-10 text-center">
            <Button variant="outline" onClick={() => navigate('/transparency')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Transparency & Accountability
            </Button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white/50 dark:bg-slate-900/50 py-6 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-slate-500 dark:text-slate-400">
          © 2025 Kaiventive Holdings. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default CorrectionsPage;
